import { baseApi } from '@store/baseApi';
import {
  enhancedApi,
  type ProviderRuleCreateRequest,
  type ProviderRuleCreateResponse,
  type ProviderRuleDeleteResponse,
  type ProviderRuleUpdateRequest,
  type ProviderRuleUpdateResponse,
} from '@store/generated/rules';

import {
  type ProviderRuleListParams,
  type ProviderRuleListResponse,
  type ProviderRuleResponse,
  type RuleSearchParams,
  type RuleSearchResponse,
} from '../types';

/**
 * Feature-specific tag types for rules
 */
const RULES_TAG_TYPES = ['Rule', 'RuleList', 'RuleSearch'] as const;

/**
 * Rules API - Manually injected endpoints for Provider Rules API
 */
export const rulesApi = baseApi
  .enhanceEndpoints({
    addTagTypes: RULES_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      // List Rules
      getProviderRules: builder.query<ProviderRuleListResponse, ProviderRuleListParams>({
        query: params => ({
          url: '/v2/provider/rules',
          params,
        }),
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ rule_id }) => ({
                  type: 'Rule' as const,
                  id: rule_id,
                })),
                { type: 'RuleList' as const, id: 'LIST' },
              ]
            : [{ type: 'RuleList', id: 'LIST' }],
      }),

      // Get Single Rule
      getProviderRuleById: builder.query<ProviderRuleResponse, string>({
        query: ruleId => `/v2/provider/rules/${ruleId}`,
        providesTags: (result, error, ruleId) => [
          { type: 'Rule', id: ruleId },
          { type: 'RuleList', id: 'LIST' },
        ],
      }),

      // Search Rules (GET query - can be cached and invalidated)
      searchProviderRules: builder.query<RuleSearchResponse, RuleSearchParams>({
        query: (params: RuleSearchParams) => ({
          url: '/v2/provider/rules/search',
          params: {
            q: params.query,
            top_k: params.top_k,
            rule_type: params.rule_type,
          },
        }),
        // Provide tags so search results are invalidated when rules change
        providesTags: [{ type: 'RuleSearch', id: 'LIST' }],
      }),

      // Create Rule
      createProviderRule: builder.mutation<ProviderRuleCreateResponse, ProviderRuleCreateRequest>({
        query: body => ({
          url: '/v2/provider/rules',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'RuleList', id: 'LIST' }],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Manually invalidate the list to ensure refetch
            dispatch(rulesApi.util.invalidateTags([{ type: 'RuleList', id: 'LIST' }]));
          } catch {
            // Error handled by mutation hook
          }
        },
      }),

      // Update Rule
      updateProviderRule: builder.mutation<
        ProviderRuleUpdateResponse,
        { ruleId: string; body: ProviderRuleUpdateRequest }
      >({
        query: ({ ruleId, body }) => ({
          url: `/v2/provider/rules/${ruleId}`,
          method: 'PUT',
          body,
        }),
        // Only invalidate the specific rule - don't invalidate RuleList to avoid unnecessary list refetch
        // This prevents the component from reloading when editing a rule
        invalidatesTags: (result, error, { ruleId }) => [{ type: 'Rule', id: ruleId }],
      }),

      // Delete Rule
      deleteProviderRule: builder.mutation<
        ProviderRuleDeleteResponse,
        { ruleId: string; ruleType: 'compliance' | 'learning' }
      >({
        query: ({ ruleId, ruleType }) => ({
          url: `/v2/provider/rules/${ruleId}`,
          method: 'DELETE',
          params: { rule_type: ruleType },
        }),
        invalidatesTags: (result, error, { ruleId }) => [
          { type: 'Rule', id: ruleId },
          { type: 'RuleList', id: 'LIST' },
        ],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Manually invalidate the list and rule to ensure refetch
            dispatch(
              rulesApi.util.invalidateTags([
                { type: 'Rule', id: _arg.ruleId },
                { type: 'RuleList', id: 'LIST' },
              ]),
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      }),
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names
export const {
  useGetProviderRulesQuery: useGetRulesQuery,
  useGetProviderRuleByIdQuery: useGetRuleQuery,
  useSearchProviderRulesQuery: useSearchRulesQuery,
  useLazySearchProviderRulesQuery: useLazySearchRulesQuery,
  useLazyGetProviderRulesQuery: useLazyGetRulesQuery,
  useLazyGetProviderRuleByIdQuery: useLazyGetRuleQuery,
  useCreateProviderRuleMutation: useCreateRuleMutation,
  useUpdateProviderRuleMutation: useUpdateRuleMutation,
  useDeleteProviderRuleMutation: useDeleteRuleMutation,
} = rulesApi;

// Export new rule management hooks from generated API
// These are available through enhancedApi (generated from OpenAPI)

export const {
  usePostV2ProviderRulesByRuleIdDisableMutation: useDisableRuleMutation,
  usePostV2ProviderRulesByRuleIdEnableMutation: useEnableRuleMutation,
  usePostV2ProviderRulesByRuleIdForkMutation: useForkRuleMutation,
  usePostV2ProviderRulesByRuleIdRevertMutation: useRevertRuleMutation,
  useGetV2ProviderRulesByRuleIdRevisionsQuery: useGetRuleRevisionsQuery,
} = enhancedApi;
